import classNames from 'classnames';
import cn from 'classnames';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { hasItem } from '@sravni/ugc/lib/specialOffers/helpers';
import { ORGANIZATION_CARD_DEFAULT_TABS_ORDER, VITRINS_OFFERS_LIST } from '@sravni/ugc/lib/specialOffers/lists';

import { PARAMS_FROM_QUERY, SpecialOffersContext } from '../../contexts/OffersPageQueryContext';
import { getPreloaderItemsCount } from '../../helpers/getPreloaderItemsCount';
import { generateSendMessageToParentWindow } from '../../helpers/sendMessageToParentWindow';
import {
  PARAMS_FROM_WRAPPER,
  useSettingCommunicationBetweenWindows,
} from '../../hooks/settingCommunicationBetweenWindows';
import { useCurrentRegion } from '../../store/hooks/locations';
import { useRequestForOrganizationDone } from '../../store/hooks/offers';
import { checkAndFetchOffers, setActiveTabType } from '../../store/reducers/offers';

import { OneTabContent } from './components/OneTabContent';
import styles from './special-offers.module.scss';

export const SpecialOffers = () => {
  const dispatch = useDispatch();
  const location = useCurrentRegion();

  const componentRef = useRef<HTMLDivElement>();
  const contentRef = useRef<HTMLDivElement>();
  const requestForOrganizationDone = useRequestForOrganizationDone();

  const { iframeId, abName, isPopup, bonusFilter, organizationId, firstTab } = useContext(SpecialOffersContext);
  const sendMessageToParentWindow = useMemo(() => generateSendMessageToParentWindow(iframeId), [iframeId]);

  const onParamsChange = (changedParam: PARAMS_FROM_QUERY | PARAMS_FROM_WRAPPER, value) => {
    const paramsForFetching = {
      location: location.route,
      locationV2: location.routeGar,
      bonusFilter,
      organizationId,
      limit: 8,
      skip: 0,
    };

    let offerTypeForFetching = [];

    if (changedParam === PARAMS_FROM_WRAPPER.activeTabType) {
      dispatch(setActiveTabType(value));
      if (!value || firstTab === value) return;
      offerTypeForFetching = [value];
    } else if (changedParam === PARAMS_FROM_WRAPPER.allTabs) {
      if (!value?.length) return;
      if (requestForOrganizationDone) return;
      offerTypeForFetching = organizationId ? ORGANIZATION_CARD_DEFAULT_TABS_ORDER : value;
    } else if (changedParam === PARAMS_FROM_QUERY.firstTab) {
      if (!value || organizationId) return;
      if (!hasItem(VITRINS_OFFERS_LIST, firstTab)) return;
      offerTypeForFetching = [value];
    }

    if (offerTypeForFetching.length) {
      dispatch(
        checkAndFetchOffers({
          ...paramsForFetching,
          offerTypes: offerTypeForFetching,
        }),
      );
    }
  };

  const { isMobile, activeTabType, maxDesktopVisibleItems, isWithLinkToAll } = useSettingCommunicationBetweenWindows({
    componentRef,
    abName,
    sendMessageToParentWindow,
    onParamsChange,
  });

  useEffect(() => {
    onParamsChange(PARAMS_FROM_QUERY.firstTab, firstTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstTab]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollLeft = 0;
    }
  }, [activeTabType?.subtype, activeTabType?.type]);

  const isNarrow = useMediaQuery({ query: '(min-width: 650px) and (max-width: 980px)' });

  const propsForContent = {
    contentRef,
    isMobile,
    preloaderItemsCount: getPreloaderItemsCount({
      isPopup,
      isNarrow,
      isMobile,
      maxDesktopVisibleItems,
    }),
    sendMessageToParentWindow,
    maxDesktopVisibleItems,
    isWithLinkToAll,
  };

  return (
    <div className={cn(styles.specialOffers, { [styles.parentWindowDesktop]: !isMobile })} ref={componentRef}>
      <div className={classNames(styles.inner)}>
        <OneTabContent {...propsForContent} />
      </div>
    </div>
  );
};
